/**
 * TemplatesTab — Copy-paste script library.
 *
 * The Derwent care platform doesn't auto-send emails — it tells the broker
 * WHEN to send (via Care Queue) and the broker copies the rendered text from
 * here into their own email client.
 *
 * For each template:
 *   1. Optional loan picker → merge fields render against a real client.
 *   2. "Copy email text" button → puts subject + body (with the advisory
 *      footer auto-appended) on the clipboard.
 *   3. Editor — saving creates a new version; old versions are kept so
 *      the historical copy is reproducible if needed.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, FileText, Mail, Save, Eye, Check, Archive, RefreshCw, Copy,
  User, ClipboardCheck, AlertCircle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const ENFORCED_EMAIL_FOOTER = (
  '\n\n—\n' +
  'Always seek professional advice. Speak with your accountant or financial planner ' +
  'before acting on any information provided.'
);

const TemplatesTab = () => {
  const { token, user } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [items, setItems] = useState([]);
  const [loans, setLoans] = useState([]);
  const [selectedLoanId, setSelectedLoanId] = useState('');  // '' = sample
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const isAdmin = ['admin', 'manager', 'broker'].includes((user?.role || '').toLowerCase());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, loanRes] = await Promise.all([
        fetch(`${backendUrl}/api/settled-loans/templates`, { headers: authHeaders }),
        fetch(`${backendUrl}/api/settled-loans?limit=500`, { headers: authHeaders }),
      ]);
      if (tplRes.ok) setItems(await tplRes.json());
      if (loanRes.ok) {
        const j = await loanRes.json();
        const list = Array.isArray(j) ? j : (j.loans || j.items || []);
        setLoans(list);
      }
    } finally { setLoading(false); }
  }, [backendUrl, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const seed = async (force = false) => {
    if (force && !window.confirm(
      'Reset the entire template library back to the on-brand spec defaults?\n\n' +
      'This wipes every saved version. Use "Refresh from spec" only when you want ' +
      'to start fresh — your edits will be lost.'
    )) return;
    setSeeding(true);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/templates/seed?force=${force}`, {
        method: 'POST', headers: authHeaders,
      });
      if (!r.ok) throw new Error('Seed failed');
      const j = await r.json();
      toast.success(j.seeded > 0
        ? `${force ? 'Reset & seeded' : 'Inserted'} ${j.seeded} template${j.seeded === 1 ? '' : 's'}`
        : 'Library already up-to-date');
      await load();
    } catch (e) { toast.error(e.message); } finally { setSeeding(false); }
  };

  // Group versions by code. Active version surfaces first.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of items) {
      if (!map.has(t.code)) map.set(t.code, []);
      map.get(t.code).push(t);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (b.version || 0) - (a.version || 0));
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  const visibleGroups = useMemo(() => {
    if (showInactive) return grouped;
    return grouped.map(([code, vers]) => [code, vers.filter((t) => t.active)]).filter(([, v]) => v.length > 0);
  }, [grouped, showInactive]);

  const selected = useMemo(() => items.find((t) => t.id === selectedId) || null, [items, selectedId]);

  if (!isAdmin) {
    return (
      <div className="py-16 text-center text-sm text-slate-500" data-testid="templates-no-access">
        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="font-semibold">Admin access required</p>
        <p className="text-xs mt-1">The template library is editable by managers and admins only.</p>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="templates-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-slate-500 max-w-2xl">
          The system tells you <span className="font-semibold text-slate-700 dark:text-slate-200">when</span> to send each
          template via the Care Queue. Pick a client below, copy the email text, paste it into your own email and send.
        </p>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600 dark:text-slate-300 inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="w-3.5 h-3.5 rounded" data-testid="templates-show-inactive" />
            Show old versions
          </label>
          <button
            type="button"
            onClick={() => seed(false)}
            disabled={seeding}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-200 text-xs font-semibold disabled:opacity-50"
            data-testid="templates-seed-btn"
            title="Insert any missing catalogue templates (preserves your edits)"
          >
            {seeding ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Sync new templates
          </button>
          <button
            type="button"
            onClick={() => seed(true)}
            disabled={seeding}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-200 text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 text-xs font-semibold disabled:opacity-50"
            data-testid="templates-reset-btn"
            title="Wipe everything and reseed from the on-brand spec"
          >
            Reset to spec defaults
          </button>
        </div>
      </div>

      {/* Loan picker — controls who the preview/copy renders for. */}
      <div className="bg-blue-50/60 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/40 rounded-2xl px-3 py-2.5 flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
          <User className="w-3.5 h-3.5 text-blue-600" />
          <span className="font-semibold">Render for:</span>
        </div>
        <select
          value={selectedLoanId}
          onChange={(e) => setSelectedLoanId(e.target.value)}
          className="flex-1 min-w-[240px] max-w-md px-3 py-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg font-medium"
          data-testid="templates-loan-picker"
        >
          <option value="">Sample client (Sarah Mitchell · NAB)</option>
          {loans.map((l) => (
            <option key={l.id} value={l.id}>
              {l.client_name}{l.lender ? ` · ${l.lender}` : ''}{l.loan_number ? ` · ${l.loan_number}` : ''}
            </option>
          ))}
        </select>
        <p className="text-[11px] text-slate-500 italic">
          Picks the merge values for {'{{client.first_name}}'}, {'{{loan.lender}}'} and {'{{owner.name}}'}.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left — template list */}
        <div className="lg:col-span-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
          <ul className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[70vh] overflow-y-auto">
            {visibleGroups.map(([code, versions]) => (
              <li key={code}>
                <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/40 text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center justify-between">
                  <span>{code}</span>
                  <Mail className="w-3 h-3" />
                </div>
                {versions.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedId(t.id)}
                    className={`w-full text-left px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors border-l-2 ${
                      selectedId === t.id ? 'bg-blue-50 dark:bg-blue-950/30 border-blue-600' : 'border-transparent'
                    }`}
                    data-testid={`template-row-${t.code}-v${t.version}`}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-mono text-slate-400">v{t.version}</span>
                      {t.active && <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">Active</span>}
                      {!t.active && <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">Archived</span>}
                    </div>
                    <div className="text-xs text-slate-700 dark:text-slate-200 mt-0.5 truncate">{t.subject || '(no subject)'}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Updated {new Date(t.updated_at).toLocaleDateString('en-AU')}</div>
                  </button>
                ))}
              </li>
            ))}
          </ul>
          {visibleGroups.length === 0 && (
            <div className="py-12 text-center text-xs text-slate-500">
              <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              No templates yet. Click &quot;Sync new templates&quot; to load the catalogue.
            </div>
          )}
        </div>

        {/* Right — editor + preview */}
        <div className="lg:col-span-2">
          {selected ? (
            <TemplateEditor
              template={selected}
              backendUrl={backendUrl}
              authHeaders={authHeaders}
              loanId={selectedLoanId}
              loans={loans}
              onSaved={async () => { await load(); }}
            />
          ) : (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl py-16 text-center text-sm text-slate-500">
              <Eye className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="font-semibold text-slate-700 dark:text-slate-200">Pick a template on the left</p>
              <p className="text-xs mt-1">You&apos;ll see the editor + live preview + a Copy button here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TemplateEditor = ({ template, backendUrl, authHeaders, loanId, loans, onSaved }) => {
  const [subject, setSubject] = useState(template.subject || '');
  const [body, setBody] = useState(template.body || '');
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState('preview'); // preview | raw
  const [rendered, setRendered] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [justCopied, setJustCopied] = useState(null); // 'all' | 'subject' | 'body'

  // Reset state when a different template is selected.
  useEffect(() => {
    setSubject(template.subject || '');
    setBody(template.body || '');
  }, [template.id]);

  const dirty = subject !== (template.subject || '') || body !== (template.body || '');

  // Fetch server-side render whenever loan or template changes (and after save).
  const renderPreview = useCallback(async () => {
    setRendering(true);
    try {
      const params = loanId ? `?loan_id=${encodeURIComponent(loanId)}` : '';
      const r = await fetch(
        `${backendUrl}/api/settled-loans/templates/${template.id}/render${params}`,
        { method: 'POST', headers: authHeaders },
      );
      if (r.ok) setRendered(await r.json());
      else setRendered(null);
    } catch { setRendered(null); }
    finally { setRendering(false); }
  }, [backendUrl, authHeaders, template.id, loanId]);

  useEffect(() => { renderPreview(); }, [renderPreview]);

  const loanLabel = useMemo(() => {
    if (!loanId) return 'sample client "Sarah Mitchell"';
    const l = loans.find((x) => x.id === loanId);
    return l ? `${l.client_name}${l.lender ? ` · ${l.lender}` : ''}` : '(loan)';
  }, [loanId, loans]);

  const copyToClipboard = async (text, label, key) => {
    try {
      // Modern path
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers / non-secure contexts.
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success(`${label} copied`);
      setJustCopied(key);
      setTimeout(() => setJustCopied(null), 1800);
    } catch (e) {
      toast.error('Copy failed — select the text manually');
    }
  };

  const save = async () => {
    if (!body.trim()) return toast.error('Body cannot be empty');
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (subject !== (template.subject || '')) params.append('subject', subject);
      if (body !== (template.body || ''))       params.append('body', body);
      const r = await fetch(`${backendUrl}/api/settled-loans/templates/${template.id}?${params}`, {
        method: 'PATCH', headers: authHeaders,
      });
      if (!r.ok) throw new Error('Save failed');
      const j = await r.json();
      toast.success(`Saved as v${j.version || (template.version + 1)} · old version archived`);
      await onSaved?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/templates/${template.id}?active=${!template.active}`, {
        method: 'PATCH', headers: authHeaders,
      });
      if (!r.ok) throw new Error('Toggle failed');
      toast.success(template.active ? 'Archived this version' : 'Reactivated this version');
      await onSaved?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const renderedSubject = rendered?.subject || subject;
  const renderedBodyWithFooter = rendered?.body_with_footer || `${body}${ENFORCED_EMAIL_FOOTER}`;
  const renderedBodyOnly = rendered?.body || body;
  const missing = rendered?.missing_fields || [];
  const fullCopy = `Subject: ${renderedSubject}\n\n${renderedBodyWithFooter}`;

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden" data-testid="template-editor">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3 flex-wrap">
        <span className="font-mono text-xs font-bold text-slate-900 dark:text-white">{template.code}</span>
        <span className="font-mono text-[10px] text-slate-400">v{template.version}</span>
        <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">{template.channel}</span>
        {template.active && <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">Active</span>}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={toggleActive}
            disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold disabled:opacity-50"
            data-testid="template-toggle-active"
          >
            <Archive className="w-3 h-3" /> {template.active ? 'Archive' : 'Reactivate'}
          </button>
          {dirty && (
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50"
              data-testid="template-save-btn"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save as v{template.version + 1}
            </button>
          )}
        </div>
      </div>

      {/* Big Copy CTA — primary action on this screen */}
      <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-950/30 dark:to-blue-950/30 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-600 dark:text-slate-300">Ready to send · for {loanLabel}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Hit Copy email text, paste into your own email client, double-check, send.
              {missing.length > 0 && (
                <span className="inline-flex items-center gap-1 ml-1 text-amber-700 dark:text-amber-400">
                  <AlertCircle className="w-3 h-3" /> Missing on this loan: {missing.join(', ')}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => copyToClipboard(renderedSubject, 'Subject', 'subject')}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-white hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold"
              data-testid="template-copy-subject"
            >
              {justCopied === 'subject' ? <ClipboardCheck className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />} Subject
            </button>
            <button
              type="button"
              onClick={() => copyToClipboard(renderedBodyWithFooter, 'Body', 'body')}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-white hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold"
              data-testid="template-copy-body"
            >
              {justCopied === 'body' ? <ClipboardCheck className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />} Body + footer
            </button>
            <button
              type="button"
              onClick={() => copyToClipboard(fullCopy, 'Email', 'all')}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm"
              data-testid="template-copy-all"
            >
              {justCopied === 'all' ? <ClipboardCheck className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copy email text
            </button>
          </div>
        </div>
      </div>

      {/* Editor + preview grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-x divide-slate-100 dark:divide-slate-800">
        {/* Source */}
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg font-medium"
              placeholder="A short, human subject line"
              data-testid="template-subject-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={16}
              className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg font-mono"
              placeholder={'Hi {{client.first_name}}, …'}
              data-testid="template-body-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Use merge fields like <code>{'{{client.first_name}}'}</code>, <code>{'{{loan.lender}}'}</code>, <code>{'{{owner.name}}'}</code>.
              Missing values just render as blank in the copy — you can fix them by hand before sending.
            </p>
          </div>
        </div>

        {/* Preview */}
        <div className="p-4 bg-slate-50/60 dark:bg-slate-900/40">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
              {rendering ? 'Rendering…' : `Preview for ${loanLabel}`}
            </span>
            <div className="inline-flex items-center bg-white dark:bg-slate-800 rounded-md p-0.5 border border-slate-200 dark:border-slate-700">
              {[{ k: 'preview', label: 'Rendered' }, { k: 'raw', label: 'Source' }].map(({ k, label }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setView(k)}
                  className={`px-2 py-0.5 text-[10px] font-semibold rounded ${view === k ? 'bg-blue-600 text-white' : 'text-slate-600 dark:text-slate-300'}`}
                  data-testid={`template-view-${k}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {view === 'preview' ? (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4 text-sm">
              <div className="border-b border-slate-100 dark:border-slate-800 pb-2 mb-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Subject</div>
                <div className="font-semibold text-slate-900 dark:text-white mt-0.5">
                  {renderedSubject || <span className="italic text-slate-400">(no subject)</span>}
                </div>
              </div>
              <div className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
                {renderedBodyOnly}
              </div>
              <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
                <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-400 mb-1 inline-flex items-center gap-1">
                  <Check className="w-3 h-3" /> Footer (auto-appended on copy)
                </div>
                <p className="text-[11px] text-slate-500 italic leading-relaxed whitespace-pre-wrap">
                  {ENFORCED_EMAIL_FOOTER.trim()}
                </p>
              </div>
            </div>
          ) : (
            <pre className="bg-slate-900 text-slate-100 text-[11px] p-3 rounded-lg overflow-x-auto whitespace-pre-wrap leading-relaxed">{body}</pre>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatesTab;
